import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { documentSerialsApi } from '../api/documentSerials';
import { invoicesApi } from '../api/invoices';
import { journalApi } from '../api/journal';
import type { DocumentSerialType } from '../types/documentSerial';

/**
 * Hook that finds the matching document serial for a given type
 * and computes the next number preview.
 *
 * When no serial is configured, falls back to detecting the pattern
 * from existing invoices or journal entries and suggesting the next number.
 *
 * Used by all document creation forms to show auto-numbering preview
 * and pass autoSerialCode to the backend.
 */
export function useDocumentSerialPreview(serialType: DocumentSerialType, date?: string) {
  const [autoFilled, setAutoFilled] = useState(false);

  const { data: serials } = useQuery({
    queryKey: ['document-serials'],
    queryFn: () => documentSerialsApi.list(),
  });

  const direction = serialType === 'SalesInvoice' ? 'Made' : 'Received';
  const isJournalType = serialType === 'JournalEntry' || serialType === 'WageSheet';

  const currentSerial = useMemo(
    () => serials?.find(s => s.serialType === serialType && !s.isObsolete) ?? null,
    [serials, serialType]
  );

  // Fallback: fetch existing invoices when no serial is configured (for invoice types)
  const { data: existingInvoices } = useQuery({
    queryKey: ['invoices-for-serial-detect', direction],
    queryFn: () => invoicesApi.list({ direction, limit: 50 }),
    enabled: !currentSerial && !isJournalType,
  });

  // Fallback: fetch existing journal entries when no serial is configured (for journal types)
  const { data: existingJournalEntries } = useQuery({
    queryKey: ['journal-for-serial-detect'],
    queryFn: () => journalApi.list({ limit: 50 }),
    enabled: !currentSerial && isJournalType,
  });

  // Get document numbers from either invoices or journal entries
  const existingDocNumbers = useMemo(() => {
    if (currentSerial) return [];

    if (isJournalType && existingJournalEntries && existingJournalEntries.length > 0) {
      return existingJournalEntries
        .map(entry => entry.documentNumber)
        .filter((n): n is string => !!n);
    }

    if (!isJournalType && existingInvoices && existingInvoices.length > 0) {
      return existingInvoices
        .map(inv => inv.documentNumber)
        .filter((n): n is string => !!n);
    }

    return [];
  }, [currentSerial, isJournalType, existingJournalEntries, existingInvoices]);

  // Detect pattern from existing document numbers when no serial found
  const detectedPattern = useMemo(() => {
    if (currentSerial || existingDocNumbers.length === 0) return null;

    // Simple client-side pattern detection: find common prefix and max numeric suffix
    // e.g. ["SF0001", "SF0002", "SF0003"] -> prefix="SF", padding=4, nextSeq=4
    const match = existingDocNumbers.reduce<{ prefix: string; padding: number; maxNum: number } | null>(
      (acc, num) => {
        // Try to extract prefix and trailing number
        const m = num.match(/^(.*?)(\d+)$/);
        if (!m || !m[1] || !m[2]) return acc;
        const prefix = m[1];
        const numStr = m[2];
        const padding = numStr.length;
        const numVal = parseInt(numStr, 10);

        if (!acc) return { prefix, padding, maxNum: numVal };
        if (acc.prefix !== prefix) return acc; // inconsistent prefix
        return { ...acc, maxNum: Math.max(acc.maxNum, numVal) };
      },
      null
    );

    return match ? { ...match, nextNum: match.maxNum + 1 } : null;
  }, [currentSerial, existingDocNumbers]);

  const previewNumber = useMemo(() => {
    // Primary: use configured serial
    if (currentSerial) {
      const nextNum = currentSerial.lastNumber + 1;
      const padding = currentSerial.sequencePadding || 6;
      const code = currentSerial.serialCode.toUpperCase();
      const dateStr: string = date || new Date().toISOString().substring(0, 10);

      if (currentSerial.pattern) {
        let result = currentSerial.pattern;
        const d = new Date(dateStr);
        result = result.replace(/\{PREFIX\}/gi, code);
        result = result.replace(/\{YYYY\}/gi, String(d.getFullYear()));
        result = result.replace(/\{YY\}/gi, String(d.getFullYear()).slice(-2));
        result = result.replace(/\{MM\}/gi, String(d.getMonth() + 1).padStart(2, '0'));
        result = result.replace(/\{DD\}/gi, String(d.getDate()).padStart(2, '0'));
        const seqMatch = result.match(/\{SEQ:(\d+)\}/i);
        if (seqMatch && seqMatch[1]) {
          result = result.replace(/\{SEQ:\d+\}/i, String(nextNum).padStart(parseInt(seqMatch[1], 10), '0'));
        } else {
          result = result.replace(/\{SEQ\}/gi, String(nextNum).padStart(padding, '0'));
        }
        return result;
      }

      if (currentSerial.dateInNumber) {
        return `${code}${dateStr.replace(/-/g, '')}-${String(nextNum).padStart(padding, '0')}`;
      }
      return `${code}${String(nextNum).padStart(padding, '0')}`;
    }

    // Fallback: use detected pattern from existing documents
    if (detectedPattern) {
      return `${detectedPattern.prefix}${String(detectedPattern.nextNum).padStart(detectedPattern.padding, '0')}`;
    }

    // Last resort: default serial code for the type
    const defaultCode = isJournalType ? 'SP'
      : serialType === 'SalesInvoice' ? 'SF'
      : serialType === 'PurchaseInvoice' ? 'SG'
      : serialType === 'CashIncome' ? 'KIO'
      : serialType === 'CashSpending' ? 'KPO'
      : serialType === 'BankOperation' ? 'B'
      : serialType === 'Offset' ? 'AV'
      : 'BD';
    return `${defaultCode}000001`;
  }, [currentSerial, date, detectedPattern, isJournalType, serialType]);

  // When using detected pattern, we can't auto-generate server-side
  // so autoSerialCode is null (the detected number will be used as explicit document number)
  const autoSerialCode = currentSerial?.serialCode ?? null;

  return {
    currentSerial,
    previewNumber,
    autoSerialCode,
    autoFilled,
    setAutoFilled,
    isDetected: !currentSerial && !!detectedPattern,
  };
}
